const REQUIRED_ACCOUNT_CSV_HEADERS = [
    "asOfDate",
    "updateAllVersions",
    "accountName",
    "accountHccId",
    "taxId",
    "topAccountHccId",
    "parentAccountHccId",
    "billParent",
    "accountType",
    "subscriptionsAllowed",
    "effectiveDate",
    "renewalInterval",
    "primaryAddress1",
    "primaryAddress2",
    "primaryAddress3",
    "primaryCity",
    "primaryState",
    "primaryZip",
    "billingAddress1",
    "billingAddress2",
    "billingAddress3",
    "billingCity",
    "billingState",
    "billingZip",
    "billingCycle",
];

const headerToColumnMap = {
    "asofdate": "asOfDate",
    "updateallversions": "updateAllVersions",
    "accountname": "accountName",
    "accounthccid": "accountHccId",
    "taxid": "taxId",
    "topaccounthccid": "topAccountHccId",
    "parentaccounthccid": "parentAccountHccId",
    "billparent?": "billParent",
    "accounttype": "accountType",
    "subscriptionsallowed?": "subscriptionsAllowed",
    "effectivedate": "effectiveDate",
    "renewalinterval(months)": "renewalInterval",
    "primarycorrespondence-addressline1": "primaryAddress1",
    "primarycorrespondence-addressline2": "primaryAddress2",
    "primarycorrespondence-addressline3": "primaryAddress3",
    "primarycorrespondence-addresscity": "primaryCity",
    "primarycorrespondence-addressstate": "primaryState",
    "primarycorrespondence-addresszip": "primaryZip",
    "premiumbilling-addressline1": "billingAddress1",
    "premiumbilling-addressline2": "billingAddress2",
    "premiumbilling-addressline3": "billingAddress3",
    "premiumbilling-addresscity": "billingCity",
    "premiumbilling-addressstate": "billingState",
    "premiumbilling-addresszip": "billingZip",
    "billingcyclename": "billingCycle",

    // UDTs
    "invoicetype": "invoiceType",
    "paymentremitinfo": "paymentRemitInfo",
    "purchaseorderid": "purchaseOrderId",
    "fundingmode": "fundingMode",
    "accountmigrationdate": "accountMigrationDate",
    "newyorkstatehcraelection": "newYorkStateHcraElection",
    "newyorkstatehcraelectiondate": "newYorkStateHcraElectionDate",
    "newyorkstatehcraelectionnotes": "newYorkStateHcraElectionNotes",
    "risksharing": "riskSharing",
    "channelpartner": "channelPartner",
};

const normalizeHeader = (h) =>
    String(h ?? "")
        .replace(/^﻿/, "")
        .replace(/\s/g, "")
        .trim()
        .toLowerCase();

const normalizeCell = (v) => {
    const s = String(v ?? "").trim();
    return s === "" ? null : s;
};

const normalizeBoolean = (v) => {
    const s = String(v ?? "").trim().toLowerCase();
    if (!s || s === "") {
        return null;
    }
    if (s === "true" || s === "y") {
        return "true";
    } else {
        return "false";
    }
}

const parseFlexibleDate = (value) => {
    if (value == null || String(value).trim() === "") return null;
    const raw = String(value).trim();
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
        return { iso: null, raw };
    }
    return d.toISOString().slice(0, 10);
};

const normalizeRow = (row) => {
    return Object.keys(row).reduce(
        (a, k) => {
            const header = headerToColumnMap[normalizeHeader(k)];
            a[header] = row[k];
            return a;
        },
        {});
};

const rowToAccount = (row, accountTypes, billingCycles, rowIndex) => {
    const norm = normalizeRow(row);

    // Validate required fields that cannot be null
    const requiredFields = {
        accountName: norm.accountName,
        accountHccId: norm.accountHccId,
        topAccountHccId: norm.topAccountHccId,
        accountType: norm.accountType,
        billingCycle: norm.billingCycle,
    };

    const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => !normalizeCell(value))
        .map(([key, _]) => key);

    if (missingFields.length > 0) {
        throw new Error(
            `Row ${rowIndex + 1}: Missing required fields: ${missingFields.join(", ")}`
        );
    }

    // Validate account type exists
    const accountType = accountTypes.find(at => at.label === normalizeCell(norm.accountType));
    if (!accountType) {
        throw new Error(
            `Row ${rowIndex + 1}: Invalid account type "${normalizeCell(norm.accountType)}". Must match one of the valid account types.`
        );
    }

    // Validate billing cycle exists
    const billingCycle = billingCycles.find(bc => bc.label === normalizeCell(norm.billingCycle));
    if (!billingCycle) {
        throw new Error(
            `Row ${rowIndex + 1}: Invalid billing cycle "${normalizeCell(norm.billingCycle)}". Must match one of the valid billing cycles.`
        );
    }

    return {
        asOfDate: parseFlexibleDate(norm.asOfDate),
        updateAllVersions: normalizeBoolean(norm.updateAllVersions),
        accountName: normalizeCell(norm.accountName),
        accountHccId: normalizeCell(norm.accountHccId),
        taxId: normalizeCell(norm.taxId),
        topAccountHccId: normalizeCell(norm.topAccountHccId),
        parentAccountHccId: normalizeCell(norm.parentAccountHccId),
        billParent: normalizeBoolean(norm.billParent),
        accountType: accountType.value,
        subscriptionsAllowed: normalizeBoolean(norm.subscriptionsAllowed),
        effectiveDate: parseFlexibleDate(norm.effectiveDate),
        renewalInterval: normalizeCell(norm.renewalInterval),
        primaryAddress1: normalizeCell(norm.primaryAddress1),
        primaryAddress2: normalizeCell(norm.primaryAddress2),
        primaryAddress3: normalizeCell(norm.primaryAddress3),
        primaryCity: normalizeCell(norm.primaryCity),
        primaryState: normalizeCell(norm.primaryState),
        primaryZip: normalizeCell(norm.primaryZip),
        billingAddress1: normalizeCell(norm.billingAddress1),
        billingAddress2: normalizeCell(norm.billingAddress2),
        billingAddress3: normalizeCell(norm.billingAddress3),
        billingCity: normalizeCell(norm.billingCity),
        billingState: normalizeCell(norm.billingState),
        billingZip: normalizeCell(norm.billingZip),
        billingCycle: billingCycle.label,
    };
};

const parseAccountsCsvData = (rows, accountTypes, billingCycles) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("CSV has no data rows.");
    }

    const normalizedRows = rows.map(normalizeRow);

    // Validate rows, since CSV all rows have same headers
    const requiredHeaders = new Set(REQUIRED_ACCOUNT_CSV_HEADERS);
    const keys = new Set(Object.keys(normalizedRows[0]));
    const missingKeys = requiredHeaders.difference(keys);
    if (missingKeys.size !== 0) {
        throw new Error(`CSV missing required rows: ${[...missingKeys].join(", ")}.`);
    }

    const accounts = rows.map((r, i) => rowToAccount(r, accountTypes, billingCycles, i));
    return accounts;
};

const CSV_HEADER = "As Of Date,Update All Versions,Account Name,Account HCC ID,TAX ID,Top Account HCC ID,Parent Account HCC ID,Bill Parent?,Account Type,Subscriptions Allowed?,Effective Date,Renewal Interval (Months),Primary Correspondence - Address Line 1,Primary Correspondence - Address Line 2,Primary Correspondence - Address Line 3,Primary Correspondence - Address City,Primary Correspondence - Address State,Primary Correspondence - Address Zip,Premium Billing - Address Line 1,Premium Billing - Address Line 2,Premium Billing - Address Line 3,Premium Billing - Address City,Premium Billing - Address State,Premium Billing - Address Zip,Billing Cycle Name,Invoice Type,Payment Remit Info,Purchase Order Id,Funding Mode,Account Migration Date,New York State HCRA Election,New York State HCRA Election Date,New York State HCRA Election Notes,Risk Sharing,Channel Partner";

export {
    parseAccountsCsvData,
    REQUIRED_ACCOUNT_CSV_HEADERS,
    CSV_HEADER,
};
